import React, { useMemo, useState } from 'react';
import useGameStore from '../store/gameStore';
import { formatMoney } from '../utils/format';
import { useTranslation } from '../i18n';
import { SUPPORT_FAMILIES, SUPPORT_ROLES, getSupportRolesByFamily, evaluatePrerequisite } from '../data/supportRoles';
import { getLocalizedSupportRole, getLocalizedSupportFamily } from '../data/supportRolesI18n';
import { INVESTMENTS, getInvestmentsBySupportFamily } from '../data/investments';
import { getBonusImpactExplainer } from '../utils/bonusImpactExplainer';
// 07/02/2026 — CommercialPerformancePanel is rendered on /strategie now.
// import CommercialPerformancePanel from '../components/strategy/CommercialPerformancePanel';
import DepotManagersPanel from '../components/depot/DepotManagersPanel';
import { toast } from '../components/ui/sonner';
import {
  Briefcase, BarChart3, Megaphone, ShoppingCart, Calculator, Users,
  Headphones, Wrench, ClipboardList, Lock, CheckCircle2, X, ArrowUpRight,
  AlertTriangle, Sparkles, Cog, TrendingUp, Building2,
} from 'lucide-react';

const FAMILY_ICONS = {
  Briefcase, BarChart3, Megaphone, ShoppingCart, Calculator, Users,
  Headphones, Wrench, ClipboardList,
};

const FAMILY_COLORS = {
  commerce: { bg: 'bg-money/10', text: 'text-money', border: 'border-money/40', accent: 'bg-money' },
  marketing: { bg: 'bg-accent/10', text: 'text-accent', border: 'border-accent/40', accent: 'bg-accent' },
  communication: { bg: 'bg-purple-500/10', text: 'text-purple-400', border: 'border-purple-500/40', accent: 'bg-purple-500' },
  achats: { bg: 'bg-amber-500/10', text: 'text-amber-400', border: 'border-amber-500/40', accent: 'bg-amber-500' },
  finance: { bg: 'bg-emerald-500/10', text: 'text-emerald-400', border: 'border-emerald-500/40', accent: 'bg-emerald-500' },
  rh: { bg: 'bg-pink-500/10', text: 'text-pink-400', border: 'border-pink-500/40', accent: 'bg-pink-500' },
  sav: { bg: 'bg-cyan-500/10', text: 'text-cyan-400', border: 'border-cyan-500/40', accent: 'bg-cyan-500' },
  garage: { bg: 'bg-orange-500/10', text: 'text-orange-400', border: 'border-orange-500/40', accent: 'bg-orange-500' },
  admin: { bg: 'bg-blue-500/10', text: 'text-blue-400', border: 'border-blue-500/40', accent: 'bg-blue-500' },
};

function RoleCard({ role: rawRole, gameState, bonusContext, hired, onHire, onFire }) {
  const { t } = useTranslation();
  const role = getLocalizedSupportRole(rawRole, t);
  const family = SUPPORT_FAMILIES.find(f => f.id === role.familyId);
  const colors = FAMILY_COLORS[role.familyId] || FAMILY_COLORS.admin;
  const Icon = FAMILY_ICONS[family?.icon] || Briefcase;
  const check = evaluatePrerequisite(role, gameState);
  const canHire = check.ok && !hired && gameState.treasury > role.monthlyCost;
  const lockedByPrereq = !check.ok;
  const treasuryShort = check.ok && !hired && gameState.treasury <= role.monthlyCost;

  return (
    <div
      className={`rounded-lg border-2 transition-all overflow-hidden ${
        hired
          ? `${colors.border} ${colors.bg}`
          : lockedByPrereq
            ? 'border-ink-600 bg-ink-700/20 opacity-70'
            : 'border-ink-600 bg-ink-800 hover:border-ink-500'
      }`}
      data-testid={`support-role-${role.id}`}
    >
      <div className="flex">
        {/* Portrait */}
        <div className="w-24 sm:w-28 flex-shrink-0 relative bg-ink-900">
          {role.portrait ? (
            <img
              src={role.portrait}
              alt={role.name || role.role}
              className="w-full h-full object-cover object-top"
              loading="lazy"
            />
          ) : (
            <div className={`w-full h-full flex items-center justify-center ${colors.bg}`}>
              <Icon className={`w-10 h-10 ${colors.text}`} />
            </div>
          )}
          {hired && (
            <div className="absolute top-1 right-1 bg-money text-ink-900 text-[9px] font-bold px-1.5 py-0.5 rounded-sm uppercase tracking-wider">
              {t('support_hired_badge')}
            </div>
          )}
        </div>

        {/* Body */}
        <div className="flex-1 p-3 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              <h4 className="font-bold text-sm text-slate-100 leading-tight truncate" data-testid={`role-name-${role.id}`}>
                {role.name || role.role}
              </h4>
              <p className="text-[11px] text-slate-400 truncate">
                {role.role}{role.age ? ` · ${role.age} ${t('support_age_suffix')}` : ''}
              </p>
              <div className="flex items-center gap-1.5 mt-1">
                <span className={`text-[9px] px-1.5 py-0.5 rounded-sm font-mono ${colors.bg} ${colors.text}`}>
                  {family?.name || role.familyId}
                </span>
                {role.level && (
                  <span className="text-[9px] text-slate-500 font-mono uppercase tracking-wider">
                    {role.level}
                  </span>
                )}
              </div>
            </div>
            <div className="text-right flex-shrink-0">
              <p className="text-[9px] text-slate-500 uppercase tracking-wider">{t('support_salary_label')}</p>
              <p className="text-sm font-black font-mono text-danger" data-testid={`role-cost-${role.id}`}>
                {formatMoney(role.monthlyCost)}<span className="text-[9px] font-normal text-slate-500">/m</span>
              </p>
            </div>
          </div>

          {role.background && (
            <p className="text-[11px] text-slate-400 mt-1.5 leading-snug italic">&laquo;&nbsp;{role.background}&nbsp;&raquo;</p>
          )}

          {/* Bonuses */}
          <ul className="mt-2 space-y-0.5">
            {(role.bonuses || []).map((b) => (
              <li key={`b-${role.id}-${b.label}`} className="flex items-start gap-1.5 text-[11px]">
                <ArrowUpRight className={`w-3 h-3 flex-shrink-0 mt-0.5 ${colors.text}`} />
                <span className="text-slate-300">{b.label}</span>
              </li>
            ))}
            {(role.unlocks || []).map((u) => (
              <li key={`u-${role.id}-${u}`} className="flex items-start gap-1.5 text-[11px]">
                <Sparkles className="w-3 h-3 flex-shrink-0 mt-0.5 text-warn" />
                <span className="text-warn">{t('support_unlocks_label')} : {u.replace(/_/g, ' ')}</span>
              </li>
            ))}
          </ul>

          {/* Prerequisite */}
          {role.prerequisite && (
            <div className={`mt-1.5 flex items-center gap-1.5 text-[10px] ${check.ok ? 'text-money' : 'text-warn'}`}>
              {check.ok ? <CheckCircle2 className="w-3 h-3" /> : <Lock className="w-3 h-3" />}
              <span className="font-mono">{role.prerequisite.label}</span>
            </div>
          )}

          {/* Action */}
          <div className="mt-2 flex items-center justify-end gap-2">
            {hired ? (
              <button
                onClick={() => onFire(role.id)}
                className="text-[11px] border border-danger/40 text-danger px-3 py-1.5 rounded-md hover:bg-danger/10 transition-colors font-mono uppercase tracking-wider flex items-center gap-1"
                data-testid={`fire-role-${role.id}`}
              >
                <X className="w-3 h-3" /> {t('support_fire_btn')}
              </button>
            ) : (
              <button
                onClick={() => onHire(role.id)}
                disabled={!canHire}
                className={`text-[11px] px-3 py-1.5 rounded-md font-mono font-bold uppercase tracking-wider transition-colors flex items-center gap-1 ${
                  !canHire
                    ? 'bg-ink-700 text-slate-600 cursor-not-allowed border border-ink-600'
                    : `${colors.accent} text-white hover:opacity-90`
                }`}
                title={lockedByPrereq ? check.label : treasuryShort ? t('support_low_treasury_full') : undefined}
                data-testid={`hire-role-${role.id}`}
              >
                {lockedByPrereq ? <Lock className="w-3 h-3" /> : treasuryShort ? <AlertTriangle className="w-3 h-3" /> : <Sparkles className="w-3 h-3" />}
                {lockedByPrereq ? t('support_locked_btn') : t('support_hire_btn')}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function ToolCard({ tool, gameState, activated, canAfford, onActivate, onDeactivate }) {
  const { t } = useTranslation();
  const colors = FAMILY_COLORS[tool.supportFamily] || FAMILY_COLORS.admin;
  const conditionMet = tool.conditionCheck ? tool.conditionCheck(gameState) : true;
  const treasuryShort = !activated && !canAfford;

  return (
    <div
      className={`rounded-lg border-2 transition-all overflow-hidden ${
        activated
          ? `${colors.border} ${colors.bg}`
          : !conditionMet
            ? 'border-ink-600 bg-ink-700/20 opacity-70'
            : 'border-ink-600 bg-ink-800 hover:border-ink-500'
      }`}
      data-testid={`support-tool-${tool.id}`}
    >
      <div className="p-3">
        <div className="flex items-start gap-3">
          <div className={`p-2 rounded ${colors.bg} ${colors.text} flex-shrink-0`}>
            <Cog className="w-4 h-4" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <h4 className="font-bold text-sm text-slate-100 leading-tight truncate">{tool.name}</h4>
                <p className="text-[10px] text-slate-500">{t('support_tool_label')} — {tool.category}</p>
              </div>
              <div className="text-right flex-shrink-0">
                <p className="text-[9px] text-slate-500 uppercase tracking-wider">{t('support_cost_label')}</p>
                <p className="text-sm font-black font-mono text-danger">
                  {tool.costBuy > 0 ? `${formatMoney(tool.costBuy)} ` : ''}
                  <span className="text-[9px] font-normal text-slate-500">+ {formatMoney(tool.chargeMonthly)}/m</span>
                </p>
              </div>
            </div>
            <ul className="mt-1.5 space-y-0.5">
              <li className="flex items-center gap-1.5 text-[11px]">
                <ArrowUpRight className={`w-3 h-3 ${colors.text}`} />
                <span className="text-slate-300">{tool.impactRevenue}</span>
              </li>
            </ul>
            {tool.condition && (
              <div className={`mt-1.5 flex items-center gap-1.5 text-[10px] ${conditionMet ? 'text-money' : 'text-warn'}`}>
                {conditionMet ? <CheckCircle2 className="w-3 h-3" /> : <Lock className="w-3 h-3" />}
                <span className="font-mono">{tool.condition}</span>
              </div>
            )}
            <div className="mt-2 flex items-center justify-end gap-2">
              {activated ? (
                <button
                  onClick={() => onDeactivate(tool.id)}
                  className="text-[11px] border border-danger/40 text-danger px-3 py-1.5 rounded-md hover:bg-danger/10 transition-colors font-mono uppercase tracking-wider flex items-center gap-1"
                  data-testid={`deactivate-tool-${tool.id}`}
                >
                  <X className="w-3 h-3" /> {t('support_deactivate_btn')}
                </button>
              ) : (
                <button
                  onClick={() => onActivate(tool.id)}
                  disabled={!conditionMet || treasuryShort}
                  className={`text-[11px] px-3 py-1.5 rounded-md font-mono font-bold uppercase tracking-wider transition-colors flex items-center gap-1 ${
                    !conditionMet || treasuryShort
                      ? 'bg-ink-700 text-slate-600 cursor-not-allowed border border-ink-600'
                      : `${colors.accent} text-white hover:opacity-90`
                  }`}
                  title={!conditionMet ? tool.condition : treasuryShort ? t('support_low_treasury_short') : undefined}
                  data-testid={`activate-tool-${tool.id}`}
                >
                  {!conditionMet ? <Lock className="w-3 h-3" /> : treasuryShort ? <AlertTriangle className="w-3 h-3" /> : <Sparkles className="w-3 h-3" />}
                  {!conditionMet ? t('support_locked_btn') : t('support_activate_btn')}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function FonctionsSupport() {
  const { t } = useTranslation();
  const {
    treasury, drivers, vehicles, totalDays, monthlyRevenues, activeInvestments,
    hiredSupport, hireSupport, fireSupport, getSupportMonthlyCost, getSupportRevenueMultiplier,
    activateInvestment, deactivateInvestment,
    reputation, notoriety,
    hqDeadlineDay, hqResignationHappened,
    activeContracts, phase,
  } = useGameStore();

  // 18/06/2026 — compte à rebours siège social.
  const hqActive = !!(activeInvestments || {})['inv-office-01'];
  const hqDaysLeft = (!hqActive && hqDeadlineDay != null)
    ? Math.max(0, hqDeadlineDay - (totalDays || 0))
    : null;

  const handleHire = (id) => {
    const result = hireSupport(id);
    if (result && !result.ok && result.reason === 'hq_required') {
      toast.error(t('hq_hire_blocked_toast'));
    }
    return result;
  };

  // 05/02/2026 (Q1 Lot 1) — Sous-onglet « Atouts recrutés » : permet à
  // l'apprenant de basculer entre la vue Recrutement (familles complètes,
  // mix recrutés + non recrutés) et une vue dédiée aux atouts/forces des
  // supports déjà embauchés. Persistance localStorage pour conserver le
  // choix entre sessions.
  const [activeTab, setActiveTab] = useState(() => {
    try {
      return localStorage.getItem('hst-support-tab') || 'recruit';
    } catch { return 'recruit'; }
  });
  const handleTab = (tab) => {
    setActiveTab(tab);
    try { localStorage.setItem('hst-support-tab', tab); } catch { /* ignore */ }
  };

  // 23/07/2026 — fix user prod : « Permis convoi exceptionnel » et
  // « Sauvegarde + audit IT » restaient verrouillés à vie — le gameState
  // passé aux cartes ne contenait ni reputation, ni activeContracts, ni
  // phase, ni hqResignationHappened (tous consommés par des conditionCheck).
  const gameState = useMemo(
    () => ({
      treasury, drivers, vehicles, totalDays, monthlyRevenues, hiredSupport,
      activeInvestments, reputation, activeContracts, phase, hqResignationHappened,
    }),
    [treasury, drivers, vehicles, totalDays, monthlyRevenues, hiredSupport,
     activeInvestments, reputation, activeContracts, phase, hqResignationHappened]
  );

  // 04/06/2026 — Saturation context for the bonus-impact explainer.
  // When reputation/notoriety/satisfaction are already near their cap, the
  // explainer tags passive bonuses as `saturated` so the player sees that
  // hiring more support won't move that needle.
  const bonusContext = useMemo(() => {
    const sat = (drivers || []).length > 0
      ? Math.round((drivers || []).reduce((a, d) => a + (d.satisfaction || 0), 0) / drivers.length)
      : 0;
    return {
      reputation: reputation || 0,
      notoriety: notoriety || 0,
      avgSatisfaction: sat,
    };
  }, [reputation, notoriety, drivers]);

  const monthlyCost = getSupportMonthlyCost();
  const revenueBonus = getSupportRevenueMultiplier();
  const hiredCount = Object.keys(hiredSupport || {}).length;

  // Group roles + tools by family — display families with at least one entry.
  const populatedFamilies = useMemo(() => {
    return SUPPORT_FAMILIES
      .map(f => ({
        ...f,
        roles: getSupportRolesByFamily(f.id),
        tools: getInvestmentsBySupportFamily(f.id),
      }))
      .filter(f => f.roles.length > 0 || f.tools.length > 0);
  }, []);

  return (
    <div className="space-y-4" data-testid="fonctions-support-page">
      <div>
        <h2 className="text-2xl font-bold text-foreground">{t('support_functions_title')}</h2>
        <p className="text-xs text-slate-500 mt-1">
          {t('support_functions_intro')}
        </p>
      </div>

      {/* 18/06/2026 — Bannière compte à rebours siège social */}
      {hqDaysLeft != null && (
        <div
          className={`rounded-lg border p-3 flex items-start gap-3 ${
            hqDaysLeft <= 7
              ? 'border-danger/40 bg-danger/10'
              : 'border-amber-500/40 bg-amber-500/10'
          }`}
          data-testid="hq-deadline-banner"
        >
          <Building2 className={`w-5 h-5 flex-shrink-0 mt-0.5 ${hqDaysLeft <= 7 ? 'text-danger' : 'text-amber-400'}`} />
          <div className="text-xs leading-relaxed">
            <p className={`font-bold ${hqDaysLeft <= 7 ? 'text-danger' : 'text-amber-300'}`} data-testid="hq-deadline-days">
              {t('hq_deadline_title', { days: hqDaysLeft })}
            </p>
            <p className="text-slate-400 mt-0.5">
              {t('hq_deadline_body')}
            </p>
          </div>
        </div>
      )}
      {hqResignationHappened && !hqActive && (
        <div
          className="rounded-lg border border-danger/40 bg-danger/10 p-3 flex items-start gap-3"
          data-testid="hq-resigned-banner"
        >
          <AlertTriangle className="w-5 h-5 text-danger flex-shrink-0 mt-0.5" />
          <div className="text-xs leading-relaxed">
            <p className="font-bold text-danger">{t('hq_resigned_title')}</p>
            <p className="text-slate-400 mt-0.5">
              {t('hq_resigned_body')}
            </p>
          </div>
        </div>
      )}

      {/* 05/02/2026 (Q1 Lot 1) — Sous-onglets : Recrutement vs Atouts recrutés */}
      <div
        className="flex items-center gap-1 border-b border-ink-600"
        data-testid="support-tabs"
        role="tablist"
      >
        <button
          type="button"
          onClick={() => handleTab('recruit')}
          className={`px-4 py-2 text-xs font-bold uppercase tracking-wider border-b-2 transition-colors ${
            activeTab === 'recruit'
              ? 'border-amber-400 text-amber-300'
              : 'border-transparent text-slate-500 hover:text-slate-300'
          }`}
          data-testid="support-tab-recruit"
          role="tab"
          aria-selected={activeTab === 'recruit'}
        >
          Recrutement{/* i18n-keep — tab label */}
        </button>
        <button
          type="button"
          onClick={() => handleTab('atouts')}
          className={`px-4 py-2 text-xs font-bold uppercase tracking-wider border-b-2 transition-colors ${
            activeTab === 'atouts'
              ? 'border-emerald-400 text-emerald-300'
              : 'border-transparent text-slate-500 hover:text-slate-300'
          }`}
          data-testid="support-tab-atouts"
          role="tab"
          aria-selected={activeTab === 'atouts'}
        >
          Atouts recrutés{/* i18n-keep — tab label */}
          {hiredCount > 0 && (
            <span className="ml-2 inline-flex items-center justify-center text-[10px] font-mono bg-emerald-900/40 text-emerald-300 rounded px-1.5 py-0.5">
              {hiredCount}
            </span>
          )}
        </button>
        {/* 08/02/2026 — Q2 user prod : sous-onglet « Impact métier »
            dédié. Auparavant intégré dans Recrutement, désormais isolé
            pour devenir la « salle de pilotage » pédagogique du joueur. */}
        <button
          type="button"
          onClick={() => handleTab('impact')}
          className={`px-4 py-2 text-xs font-bold uppercase tracking-wider border-b-2 transition-colors ${
            activeTab === 'impact'
              ? 'border-amber-400 text-amber-300'
              : 'border-transparent text-slate-500 hover:text-slate-300'
          }`}
          data-testid="support-tab-impact"
          role="tab"
          aria-selected={activeTab === 'impact'}
        >
          Impact métier{/* i18n-keep — tab label */}
          {hiredCount > 0 && (
            <span className="ml-2 inline-flex items-center justify-center text-[10px] font-mono bg-amber-900/40 text-amber-300 rounded px-1.5 py-0.5">
              {hiredCount}
            </span>
          )}
        </button>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <div className="bg-ink-800 rounded-lg border border-ink-600 p-3 text-center">
          <p className="text-xs text-slate-500">{t('support_hired_count_label')}</p>
          <p className="text-xl font-bold text-foreground" data-testid="support-hired-count">{hiredCount}</p>
        </div>
        <div className="bg-ink-800 rounded-lg border border-ink-600 p-3 text-center">
          <p className="text-xs text-slate-500">{t('support_payroll_label')}</p>
          <p className="text-lg font-bold text-danger" data-testid="support-monthly-cost">{formatMoney(monthlyCost)}/m</p>
        </div>
        <div className="bg-ink-800 rounded-lg border border-ink-600 p-3 text-center">
          <p className="text-xs text-slate-500">{t('support_revenue_bonus_label')}</p>
          <p className="text-lg font-bold text-money" data-testid="support-revenue-bonus">+{Math.round(revenueBonus * 100)} %</p>
        </div>
        <div className="bg-ink-800 rounded-lg border border-ink-600 p-3 text-center">
          <p className="text-xs text-slate-500">{t('support_families_label')}</p>
          <p className="text-xl font-bold text-foreground">
            {new Set(Object.keys(hiredSupport || {}).map(id => SUPPORT_ROLES.find(r => r.id === id)?.familyId).filter(Boolean)).size}
            <span className="text-sm text-slate-500"> / {populatedFamilies.length}</span>
          </p>
        </div>
      </div>

      {/* TAB CONTENT: Atouts recrutés */}
      {activeTab === 'atouts' && (
        <AtoutsRecrutesPanel hiredSupport={hiredSupport} bonusContext={bonusContext} />
      )}

      {/* 08/02/2026 — TAB CONTENT: Impact métier (déplacé depuis Recrutement). */}
      {activeTab === 'impact' && (
        hiredCount > 0
          ? <HiredImpactPanel hiredSupport={hiredSupport} bonusContext={bonusContext} />
          : (
            <div className="surface-card p-6 text-center text-slate-500" data-testid="impact-empty">
              <p className="text-sm">{/* i18n-keep */}Aucun support recruté pour le moment.</p>
              <p className="text-xs mt-1">{/* i18n-keep */}Recrutez via l&apos;onglet « Recrutement » pour voir l&apos;impact métier ici.</p>
            </div>
          )
      )}

      {/* TAB CONTENT: Recrutement (vue complète) */}
      {activeTab === 'recruit' && (
        <>
          {/* 08/02/2026 — Q2 user prod : « Impact métier concret » déplacé
              vers son propre sous-onglet (data-testid: support-tab-impact). */}

          {/* 07/02/2026 — P1 retour user : "Performance commerciale" est
              déplacée vers /strategie (l'utilisateur s'attend à la trouver
              dans l'analyse stratégique, pas dans le hub recrutement
              support). Le composant `CommercialPerformancePanel` est
              désormais monté sur Strategie.jsx. */}

          {/* 05/02/2026 — Q1 retour user prod : section Responsables de dépôt
              (déplacée depuis la page Dépôt). Profils dispos uniquement
              dans NewDepotModal/AcquisitionModal au moment du choix. */}
          <DepotManagersPanel />

          {/* Families */}
          {populatedFamilies.map(family => {
            const colors = FAMILY_COLORS[family.id] || FAMILY_COLORS.admin;
            const Icon = FAMILY_ICONS[family.icon] || Briefcase;
            return (
              <div
                key={family.id}
                className="surface-card overflow-hidden"
                data-testid={`support-family-${family.id}`}
              >
                <div className={`flex items-center justify-between px-4 py-3 border-b border-ink-600 ${colors.bg}`}>
                  <div className="flex items-center gap-2">
                    <Icon className={`w-4 h-4 ${colors.text}`} />
                    <h3 className={`font-display text-sm font-bold tracking-tight uppercase ${colors.text}`}>
                      {getLocalizedSupportFamily(family, t).name}
                    </h3>
                  </div>
                  <span className="font-mono text-[10px] text-slate-500 tabular">
                    [{family.roles.length + family.tools.length} {(family.roles.length + family.tools.length) > 1 ? t('support_entries_plural') : t('support_entries_singular')}]
                  </span>
                </div>
                <div className="p-3 grid gap-2.5">
                  {family.roles.map(role => (
                    <RoleCard
                      key={role.id}
                      role={role}
                      gameState={gameState}
                      bonusContext={bonusContext}
                      hired={!!(hiredSupport || {})[role.id]}
                      onHire={(id) => handleHire(id)}
                      onFire={(id) => fireSupport(id)}
                    />
                  ))}
                  {family.tools.map(tool => (
                    <ToolCard
                      key={tool.id}
                      tool={tool}
                      gameState={gameState}
                      activated={!!(activeInvestments || {})[tool.id]}
                      canAfford={treasury >= (tool.costBuy || 0) + (tool.chargeMonthly || 0)}
                      onActivate={(id) => activateInvestment(id)}
                      onDeactivate={(id) => deactivateInvestment(id)}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </>
      )}
    </div>
  );
}

/**
 * HiredImpactPanel — 03/06/2026
 *
 * Recap pédagogique de l'impact métier concret de chaque support déjà
 * recruté. Présente, pour chaque embauche : le nom + rôle, et la liste
 * des bonus traduits en KPI business observable (marge brute, TCO, BFR,
 * cycle de paiement, etc.).
 *
 * Objectif : aider l'apprenant à relier chaque dépense RH à un effet
 * P&L mesurable, et faciliter les décisions de licenciement.
 */
function HiredImpactPanel({ hiredSupport, bonusContext }) {
  const hiredRoles = Object.keys(hiredSupport || {})
    .map(id => SUPPORT_ROLES.find(r => r.id === id))
    .filter(Boolean);
  if (hiredRoles.length === 0) return null;

  return (
    <div className="surface-card overflow-hidden" data-testid="hired-impact-panel">
      <div className="flex items-center justify-between px-4 py-3 border-b border-ink-600 bg-amber-500/5">
        <div className="flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-amber-400" />
          <h3 className="font-display text-sm font-bold tracking-tight uppercase text-amber-300">
            Impact métier concret
          </h3>
        </div>
        <span className="font-mono text-[10px] text-slate-500 tabular">
          [{hiredRoles.length} support{hiredRoles.length > 1 ? 's' : ''} actif{hiredRoles.length > 1 ? 's' : ''}]
        </span>
      </div>
      <div className="p-3 space-y-2.5">
        {hiredRoles.map(role => (
          <div
            key={role.id}
            className="rounded-md border border-ink-600 bg-ink-700/30 p-3"
            data-testid={`hired-impact-${role.id}`}
          >
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-300 flex-shrink-0" />
              <p className="text-sm font-bold text-slate-100">{role.name}</p>
              <span className="text-[10px] text-slate-500 italic">— {role.role}</span>
            </div>
            {(role.bonuses || []).length === 0 ? (
              <p className="text-xs text-slate-500 italic ml-5">
                Soutien général — impact diffus sur la performance globale.
              </p>
            ) : (
              <ul className="space-y-1.5 ml-5">
                {(role.bonuses || []).map((b, i) => {
                  const impact = getBonusImpactExplainer(b, bonusContext);
                  return (
                    <li
                      key={`${role.id}-bonus-${i}-${(b?.label || b?.type || '').slice(0, 24)}`}
                      className={`text-[11px] leading-relaxed ${impact.saturated ? 'text-slate-400 opacity-70' : 'text-slate-200'}`}
                      data-testid={`impact-${role.id}-${i}`}
                    >
                      <span className={`font-bold ${impact.saturated ? 'text-amber-500/60 line-through' : 'text-amber-200'}`}>{impact.kpi}</span>
                      <span className="text-slate-500"> — </span>
                      <span className={impact.saturated ? 'text-slate-400' : 'text-slate-300'}>{impact.detail}</span>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * 05/02/2026 (Q1 Lot 1) — AtoutsRecrutesPanel : vue dédiée aux atouts/forces
 * des supports recrutés (sous-onglet « Atouts recrutés »).
 *
 * Regroupe par famille les supports déjà embauchés et liste leurs bonus
 * traduits en KPI métier. Si rien n'est recruté → message d'incitation
 * (« Aucun support recruté pour le moment »). Sinon → grille compacte
 * avec les principaux atouts visibles d'un coup d'œil.
 */
function AtoutsRecrutesPanel({ hiredSupport, bonusContext }) {
  const hiredRoles = Object.keys(hiredSupport || {})
    .map(id => SUPPORT_ROLES.find(r => r.id === id))
    .filter(Boolean);

  if (hiredRoles.length === 0) {
    return (
      <div
        className="surface-card p-8 text-center"
        data-testid="atouts-recrutes-empty"
      >
        <Lock className="w-10 h-10 text-slate-600 mx-auto mb-3" />
        <h3 className="text-sm font-bold text-slate-300 mb-1">
          Aucun support recruté pour le moment{/* i18n-keep — placeholder narrative */}
        </h3>
        <p className="text-xs text-slate-500 max-w-md mx-auto">
          {/* i18n-keep — placeholder narrative */}
          Basculez sur l&apos;onglet « Recrutement » pour embaucher vos premiers talents support.
        </p>
      </div>
    );
  }

  // Group hired roles by family for cleaner display.
  const byFamily = hiredRoles.reduce((acc, role) => {
    const fId = role.familyId || 'other';
    if (!acc[fId]) acc[fId] = [];
    acc[fId].push(role);
    return acc;
  }, {});
  const familyOrder = Object.keys(byFamily);

  return (
    <div className="space-y-3" data-testid="atouts-recrutes-panel">
      <div className="surface-card overflow-hidden">
        <div className="flex items-center gap-2 px-4 py-3 border-b border-ink-600 bg-emerald-500/5">
          <Sparkles className="w-4 h-4 text-emerald-400" />
          <h3 className="font-display text-sm font-bold tracking-tight uppercase text-emerald-300">
            Atouts de vos {hiredRoles.length} support{hiredRoles.length > 1 ? 's' : ''} recruté{hiredRoles.length > 1 ? 's' : ''}{/* i18n-keep */}
          </h3>
        </div>
        <div className="p-3 space-y-3">
          {familyOrder.map(familyId => {
            const family = SUPPORT_FAMILIES.find(f => f.id === familyId);
            const colors = FAMILY_COLORS[familyId] || FAMILY_COLORS.admin;
            const Icon = FAMILY_ICONS[family?.icon] || Briefcase;
            const roles = byFamily[familyId] || [];
            return (
              <div
                key={familyId}
                className={`rounded-md border border-ink-600 ${colors.bg}`}
                data-testid={`atouts-family-${familyId}`}
              >
                <div className="flex items-center gap-2 px-3 py-2 border-b border-ink-600/50">
                  <Icon className={`w-3.5 h-3.5 ${colors.text}`} />
                  <h4 className={`text-[11px] font-bold uppercase tracking-wider ${colors.text}`}>
                    {family ? family.name : familyId/* i18n-keep — fallback raw id */}
                  </h4>
                  <span className="ml-auto text-[10px] font-mono text-slate-500">
                    {roles.length} pers.{/* i18n-keep */}
                  </span>
                </div>
                <div className="p-3 grid gap-2 md:grid-cols-2">
                  {roles.map(role => (
                    <div
                      key={role.id}
                      className="bg-ink-800/40 rounded p-2.5 border border-ink-600/30"
                      data-testid={`atouts-role-${role.id}`}
                    >
                      <div className="flex items-center gap-2 mb-1.5">
                        <CheckCircle2 className="w-3 h-3 text-emerald-300 flex-shrink-0" />
                        <p className="text-xs font-bold text-slate-100">{role.name/* i18n-keep — person name */}</p>
                      </div>
                      <p className="text-[10px] text-slate-500 italic mb-2 ml-5">
                        {role.role/* i18n-keep — role title */}
                      </p>
                      <ul className="space-y-1 ml-5">
                        {(role.bonuses || []).slice(0, 4).map((b, i) => {
                          const impact = getBonusImpactExplainer(b, bonusContext);
                          return (
                            <li
                              key={`${role.id}-atout-${i}-${(b?.label || b?.type || '').slice(0, 16)}`}
                              className={`text-[10px] leading-snug ${impact.saturated ? 'text-slate-500 opacity-70' : 'text-slate-200'}`}
                              data-testid={`atout-${role.id}-${i}`}
                            >
                              <span className={`font-mono font-bold ${impact.saturated ? 'text-amber-500/60 line-through' : 'text-amber-200'}`}>
                                {impact.kpi}
                              </span>
                              {impact.detail && (
                                <span className="text-slate-400 ml-1">— {impact.detail}</span>
                              )}
                            </li>
                          );
                        })}
                        {(role.bonuses || []).length > 4 && (
                          <li className="text-[9px] text-slate-500 italic">
                            +{role.bonuses.length - 4} autre(s) atout(s){/* i18n-keep */}
                          </li>
                        )}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
